<template>
  <div class="flex h-screen bg-gray-50 overflow-hidden">
    <SideBar class="flex-shrink-0" />

    <div class="flex-1 flex flex-col h-full min-h-0">
      <div class="flex-1 p-4 md:p-6 flex flex-col min-h-0">
        <div class="max-w-7xl mx-auto w-full flex flex-col h-full min-h-0">
          
          <!-- Header & Progress -->
          <div class="flex-shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div class="flex items-center gap-4">
              <h1 class="text-2xl font-bold text-gray-900">Daily Call Review</h1>
              <span v-if="!loading && !completed && calls.length > 0" class="px-3 py-1 rounded-full bg-gray-100 text-gray-600 font-medium text-sm border border-gray-200">
                Call {{ currentIndex + 1 }} of {{ calls.length }}
              </span>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="flex flex-col items-center justify-center flex-1">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p class="text-gray-500 mt-4">Loading your daily review...</p>
          </div>

          <!-- Completion State -->
          <div v-else-if="completed" class="flex-1 flex items-center justify-center">
            <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-10 text-center max-w-lg w-full">
              <div class="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 class="text-2xl font-bold text-gray-900 mb-2">You're all set!</h2>
              <p class="text-gray-500 mb-8">You've completed today's call review. You can now access the dialer.</p>
              
              <button 
                @click="goToDialer"
                class="w-full py-3 bg-primary text-black rounded-xl font-medium hover:bg-primary/90 transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <span>Open Dialer</span>
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Review Card -->
          <div v-else-if="currentCall" class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col flex-1 min-h-0">
            
            <!-- Call Info Header (Fixed) -->
            <div class="flex-shrink-0 p-6 border-b border-gray-200 bg-gray-50/50 flex flex-wrap justify-between items-center gap-4">
               <div>
                 <p class="text-sm text-gray-500 uppercase tracking-wider font-semibold mb-1">Score</p>
                 <div class="flex items-baseline gap-1">
                   <span class="text-3xl font-bold" :class="getScoreColor(currentCall.score)">{{ currentCall.score }}</span>
                   <span class="text-sm text-gray-400">/100</span>
                 </div>
               </div>
               
               <div class="text-right">
                  <p class="text-sm text-gray-500 mb-1">{{ formatDate(currentCall.started_at) }}</p>
                  <div class="text-xs font-mono text-gray-400 bg-white px-2 py-1 rounded border border-gray-200 inline-block">ID: {{ currentCall.call_id || currentCall.id }}</div>
               </div>
            </div>

            <!-- Content Grid (Scrollable) -->
            <div class="grid grid-cols-1 md:grid-cols-2 flex-1 min-h-0">
              
              <!-- Left: Playback & Feedback -->
              <div class="flex flex-col min-h-0 border-b md:border-b-0 md:border-r border-gray-200">
                 <div class="flex-1 overflow-y-auto p-6">
                    <h3 class="text-sm font-bold text-gray-900 uppercase mb-4 flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        </svg>
                        Call Recording
                     </h3>
                     
                     <div class="bg-gray-100 rounded-lg p-3 mb-6">
                       <audio controls class="w-full h-10" :src="currentCall.recording_url">
                         Your browser does not support the audio element.
                       </audio>
                     </div>

                     <h3 class="text-sm font-bold text-gray-900 uppercase mb-3 flex items-center gap-2">
                       <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                         <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                       </svg>
                       Feedback
                     </h3>
                      <div class="feedback-content text-sm text-gray-800 bg-gray-50 p-4 rounded-lg border border-gray-100">
                         <div v-html="renderedFeedback"></div>
                     </div>
                 </div>
              </div>

              <!-- Right: Interactive Quiz -->
              <div class="flex flex-col min-h-0 bg-gray-50/30">
                 <!-- Quiz Content -->
                 <div v-if="currentQuiz" class="flex-1 flex flex-col min-h-0 p-6 overflow-y-auto">
                    <!-- Progress & Streak -->
                    <div class="mb-6">
                      <div class="flex items-center justify-between mb-2">
                        <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Daily Goal</div>
                        <div v-if="streak > 1" class="flex items-center gap-1.5 text-orange-600 animate-in fade-in zoom-in duration-300">
                           <span class="text-base">🔥</span>
                           <span class="font-bold text-xs">{{ streak }} Streak</span>
                        </div>
                      </div>
                      
                      <div class="h-2.5 w-full bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          class="h-full bg-gradient-to-r from-emerald-400 to-primary transition-all duration-500 ease-out relative"
                          :style="{ width: `${progressPercentage}%` }"
                        >
                           <div class="absolute inset-0 bg-white/20"></div>
                        </div>
                      </div>
                      <div class="mt-1 text-right text-xs text-gray-400 font-medium">{{ Math.round(progressPercentage) }}% Complete</div>
                    </div>

                    <div class="mb-4">
                     <div class="flex justify-between items-center mb-2">
                       <span class="inline-block px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-800 uppercase tracking-wide">Question</span>
                       <span class="text-xs font-medium text-gray-400" v-if="currentCall.quiz.length > 1">
                         {{ currentQuestionIndex + 1 }} of {{ currentCall.quiz.length }}
                       </span>
                     </div>
                       <h3 class="text-lg font-medium text-gray-900 leading-snug">{{ currentQuiz.question }}</h3>
                    </div>

                    <div class="space-y-3">
      <button 
        v-for="(option, idx) in currentQuiz.options" 
        :key="idx"
        @click="selectOption(option)"
        :disabled="quizSubmitted"
        class="w-full text-left p-4 rounded-xl border transition-all duration-200 relative group"
        :class="getOptionClass(option)"
      >
        <div class="flex items-start gap-3">
          <div class="h-5 w-5 mt-0.5 rounded-full border flex items-center justify-center shrink-0 transition-colors"
            :class="getOptionRadioClass(option)"
          >
            <div class="h-2.5 w-2.5 rounded-full bg-current opacity-0 transition-opacity" :class="{'opacity-100': selectedOption === option}"></div>
          </div>
          <span class="text-sm font-medium">{{ option }}</span>
        </div>
      </button>
    </div>

    <!-- Feedback/Next Actions -->
    <div v-if="quizSubmitted" class="mt-6 pt-6 border-t border-gray-200 animate-in slide-in-from-bottom-2 fade-in duration-300">
      <div v-if="isCorrect" class="p-4 bg-green-50 border border-green-200 rounded-lg mb-4 flex gap-3">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p class="font-bold text-green-800 text-sm">Correct Answer</p>
          <p class="text-xs text-green-700 mt-0.5">Good catch! You can now proceed.</p>
        </div>
      </div>
      
      <div v-else class="p-4 bg-red-50 border border-red-200 rounded-lg mb-4 flex gap-3">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div>
          <p class="font-bold text-red-800 text-sm">Incorrect</p>
          <p class="text-xs text-red-700 mt-0.5">Review the feedback and try again.</p>
        </div>
      </div>

      <button 
        v-if="isCorrect"
        @click="handleNext"
        class="w-full py-3 bg-primary text-black hover:bg-primary/90 rounded-lg font-bold transition-colors shadow-sm"
      >
        {{ nextButtonText }}
      </button>
      <button 
        v-else
        @click="retryQuiz"
        class="w-full py-3 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-bold transition-colors shadow-sm"
      >
        Try Again
      </button>
    </div>
  </div>
</div>
</div>
</div>

</div>
</div>
</div>
</div>
</template>

<script>
import SideBar from '@/components/SideBar.vue'
import { getAnalyzedCalls, completeQuizQuestion } from '@/api/calls'
import { useRouter } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import { marked } from 'marked'


export default {
name: 'CallReviewView',
components: {
SideBar
},
setup() {
const router = useRouter()
const calls = ref([])
const currentIndex = ref(0)
const currentQuestionIndex = ref(0)
const loading = ref(true)
const selectedOption = ref(null)
const quizSubmitted = ref(false)
const isCorrect = ref(false)

// Current derived state
const currentCall = computed(() => calls.value[currentIndex.value])
// Get the specific quiz question based on currentQuestionIndex
const currentQuiz = computed(() => currentCall.value?.quiz?.[currentQuestionIndex.value] || null)
    const isLastCall = computed(() => currentIndex.value === calls.value.length - 1)
    const isLastQuestion = computed(() => {
      if (!currentCall.value?.quiz) return true
      return currentQuestionIndex.value === currentCall.value.quiz.length - 1
    })

    const streak = ref(0)

    const totalQuestions = computed(() => {
      return calls.value.reduce((sum, call) => sum + (call.quiz?.length || 0), 0)
    })

    const completedQuestionsCount = computed(() => {
      let count = 0
      // Previous calls
      for (let i = 0; i < currentIndex.value; i++) {
        count += calls.value[i].quiz?.length || 0
      }
      // Current call previous questions
      count += currentQuestionIndex.value
      // Current question if answered correctly and submitted
      if (quizSubmitted.value && isCorrect.value) {
        count += 1
      }
      return count
    })

    const progressPercentage = computed(() => {
        if (totalQuestions.value === 0) return 0
        return Math.min(100, (completedQuestionsCount.value / totalQuestions.value) * 100)
    })

    const completed = computed(() => !loading.value && calls.value.length > 0 && currentIndex.value >= calls.value.length)

const nextButtonText = computed(() => {
  if (!isLastQuestion.value) return 'Next Question'
  return isLastCall.value ? 'Finish Review' : 'Next Call'
})

// Parse markdown feedback
const renderedFeedback = computed(() => {
if (!currentCall.value?.feedback_summary) return ''
// Handle potentially escaped newlines from API
const text = currentCall.value.feedback_summary.replace(/\\n/g, '\n')
return marked.parse(text, { breaks: true, gfm: true })
})

onMounted(async () => {
try {
const response = await getAnalyzedCalls()
calls.value = response.data || []

// If no calls to review, skip straight to completed
if (calls.value.length === 0) {
    router.push('/dialer')
}
} catch (error) {
console.error('Failed to fetch analyzed calls:', error)
} finally {
loading.value = false
}
})

const getScoreColor = (score) => {
if (score >= 80) return 'text-green-600'
if (score >= 60) return 'text-amber-500'
return 'text-red-500'
}

const formatDate = (dateString) => {
if (!dateString) return ''
return new Date(dateString).toLocaleDateString(undefined, {
month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
})
}

const selectOption = (option) => {
if (quizSubmitted.value) return
selectedOption.value = option
submitQuiz()
}

    const submitQuiz = async () => {
        if (!selectedOption.value || !currentQuiz.value) return
        
        const correct = selectedOption.value === currentQuiz.value.correct_answer
        
        if (correct) {
          streak.value++
          try {
             // If quiz ID is available use it, otherwise fallback (or log error if critical)
             const questionId = currentQuiz.value.id
             if (questionId) {
                await completeQuizQuestion(questionId)
             } else {
                 console.warn('No quiz ID found for current question, cannot mark complete.')
             }
          } catch (e) {
            console.error('Failed to submit review', e)
          }
        } else {
          streak.value = 0
        }

        quizSubmitted.value = true
        isCorrect.value = correct
    }

const retryQuiz = () => {
selectedOption.value = null
quizSubmitted.value = false
isCorrect.value = false
}

const handleNext = () => {
  if (!isLastQuestion.value) {
    // Move to next question in same call
    currentQuestionIndex.value++
  } else {
    // Move to next call
    currentIndex.value++
    currentQuestionIndex.value = 0
  }
  
  // Reset quiz state
  selectedOption.value = null
  quizSubmitted.value = false
  isCorrect.value = false
}

const goToDialer = () => {
router.push('/dialer')
}

// UI Helpers
const getOptionClass = (option) => {
if (!quizSubmitted.value) {
    return selectedOption.value === option 
        ? 'border-blue-500 bg-blue-50 text-blue-900 ring-1 ring-blue-500' 
        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700'
}

// Submitted State logic
if (isCorrect.value && option === currentQuiz.value.correct_answer) {
    return 'border-green-500 bg-green-50 text-green-900 ring-1 ring-green-500'
}
if (selectedOption.value === option && !isCorrect.value) {
    return 'border-red-500 bg-red-50 text-red-900'
}
return 'border-gray-200 opacity-50 text-gray-500'
}

const getOptionRadioClass = (option) => {
if (!quizSubmitted.value) {
      return selectedOption.value === option ? 'border-blue-500 text-blue-600' : 'border-gray-300 text-transparent'
}
  if (isCorrect.value && option === currentQuiz.value.correct_answer) {
    return 'border-green-500 text-green-600'
}
if (selectedOption.value === option && !isCorrect.value) {
      return 'border-red-500 text-red-600'
}
return 'border-gray-300 text-transparent'
}

return {
calls,
currentCall,
currentQuiz,
currentIndex,
currentQuestionIndex,
loading,
completed,
selectedOption,
quizSubmitted,
isCorrect,
isLastCall,
isLastQuestion,
nextButtonText,
getScoreColor,
formatDate,
selectOption,
retryQuiz,
handleNext,
goToDialer,
getOptionClass,
getOptionRadioClass,
renderedFeedback,
streak,
progressPercentage
}
}
}
</script>

<style scoped>
/* Optional: Customize audio player further if needed, for now standard browser control is laid out cleanly */
audio {
  height: 40px;
}

/* Manual typography styles since @tailwindcss/typography is not installed */
.feedback-content :deep(p) {
  margin-bottom: 1em; /* Ensure paragraphs have spacing */
}
.feedback-content :deep(p):last-child {
  margin-bottom: 0;
}
.feedback-content :deep(br) {
   display: block; /* Ensure breaks are respected */
   content: "";
   margin: 1em 0;
}
.feedback-content :deep(ul), .feedback-content :deep(ol) {
  margin-bottom: 1em;
  padding-left: 1.5em;
  list-style-type: disc;
}
.feedback-content :deep(li) {
  margin-bottom: 0.25em;
}
.feedback-content :deep(strong) {
  font-weight: 600;
  color: #111827; 
}
.feedback-content :deep(h1), 
.feedback-content :deep(h2), 
.feedback-content :deep(h3) {
  font-weight: 700;
  color: #111827;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
  font-size: 1.1em;
}
</style>
